import { body, coverBg, edge, fade, gloss, mix, n, rng, shade, shadowInk, star, tint, warm, type ArtPal } from './kit';

/**
 * Mahjong Hand (麻将胡牌): a fan of seven tiles standing on red lacquer backs, faces drawn as pure geometry —
 * circles (筒), sticks (条) and the empty frame of the white dragon (白); the one-circle tile in the middle as the
 * focus, gold sparkles around. Placeholder art until the final graphics — no glyphs, no letters, no numbers
 * (the characters appear only on the in-game tiles, art/mahjong.ts); see docs/art-prompts.md (no clocks, no green
 * hats, no white/black mourning colours, no groups of four — hence three / five / nine pips, never four).
 */
type Face = 'p1' | 'p3' | 'p5' | 'p9' | 's2' | 's3' | 'wh';

const TW = 66; // tile face
const TH = 90;

export function mahjongCover(P: ArtPal, p: string): string {
  const cx = 430; // pivot of the fan (below the scene)
  const cy = 520;
  const R = 250; // pivot → tile bottom
  const S = 0.86; // the whole fan is drawn at this scale around the focus
  const fy = 200; // focus height
  const faces: Face[] = ['s3', 'p5', 'wh', 'p1', 's2', 'p3', 'p9'];
  const step = 11; // degrees between tiles
  const top = P.dark ? mix(P.bg, P.red, 0.1) : tint(P.red, 0.92);
  const mid = P.dark ? mix(P.panel, P.gold, 0.06) : tint(P.gold, 0.9);
  const bg = coverBg(p, P, top, mid, P.bg, [cx, fy]);
  const red = P.red;
  const gold = warm(P);
  const ivory = P.dark ? '#F3EAD6' : '#FBF4E4';
  const ivoryEdge = shade(ivory, P.dark ? 0.35 : 0.25);
  const lacquer = P.dark ? shade(red, 0.25) : red;
  const blue = P.dark ? shade(P.blue, 0.1) : P.blue;
  const jade = P.dark ? shade(P.teal, 0.35) : P.teal;

  // one pip of the circles suit: coloured ring, ivory gap, coloured core
  const pip = (x: number, y: number, r: number, c: string) =>
    `<circle cx="${n(x)}" cy="${n(y)}" r="${n(r)}" fill="${c}"/>` +
    `<circle cx="${n(x)}" cy="${n(y)}" r="${n(r * 0.62)}" fill="${ivory}"/>` +
    `<circle cx="${n(x)}" cy="${n(y)}" r="${n(r * 0.32)}" fill="${c}"/>`;
  // one stick of the bamboo suit: a rounded rod with two joints
  const stick = (x: number, y: number, h: number, c: string) =>
    `<rect x="${n(x - 4)}" y="${n(y - h / 2)}" width="8" height="${n(h)}" rx="4" fill="${c}"/>` +
    `<path d="M${n(x - 4)} ${n(y - h / 6)}h8M${n(x - 4)} ${n(y + h / 6)}h8" stroke="${ivory}" stroke-width="1.6"/>`;

  // face markup in tile coordinates (0,0 = top-left of the face)
  const face = (f: Face): string => {
    const mx = TW / 2;
    const my = TH / 2;
    switch (f) {
      case 'p1':
        return (
          `<circle cx="${mx}" cy="${my}" r="25" fill="url(#${p}md)" stroke="${edge(P.gold, P)}" stroke-width="1.5"/>` +
          `<circle cx="${mx}" cy="${my}" r="17" fill="${ivory}"/>` +
          `<circle cx="${mx}" cy="${my}" r="13" fill="${red}"/>` +
          `<circle cx="${mx}" cy="${my}" r="5.5" fill="${gold}"/>` +
          `<polygon points="${star(mx, my, 21, 19, 9)}" fill="none" stroke="${gold}" stroke-opacity=".7" stroke-width="1.2"/>`
        );
      case 'p3':
        return pip(17, 20, 9, blue) + pip(mx, my, 9, red) + pip(TW - 17, TH - 20, 9, jade);
      case 'p5':
        return pip(18, 21, 9, blue) + pip(TW - 18, 21, 9, jade) + pip(mx, my, 9.5, red) + pip(18, TH - 21, 9, jade) + pip(TW - 18, TH - 21, 9, blue);
      case 'p9': {
        let s = '';
        for (let r = 0; r < 3; r++) for (let c = 0; c < 3; c++) s += pip(15 + c * 18, 20 + r * 25, 7, r === 1 ? red : r === 0 ? blue : jade);
        return s;
      }
      case 's2':
        return stick(mx, 26, 30, jade) + stick(mx, TH - 26, 30, blue);
      case 's3':
        return stick(mx, 26, 30, red) + stick(20, TH - 26, 30, jade) + stick(TW - 20, TH - 26, 30, jade);
      case 'wh':
        // the white dragon: an empty frame, blue with a gold inner line
        return (
          `<rect x="12" y="14" width="${TW - 24}" height="${TH - 28}" rx="3" fill="none" stroke="${blue}" stroke-width="4"/>` +
          `<rect x="18" y="20" width="${TW - 36}" height="${TH - 40}" rx="2" fill="none" stroke="${gold}" stroke-width="1.5"/>`
        );
    }
  };

  let tiles = '';
  const k = faces.length;
  const mid0 = (k - 1) / 2;
  // outer tiles first, the focus (middle) tile last — on top of its neighbours
  const order = faces.map((_, i) => i).sort((x, y) => Math.abs(y - mid0) - Math.abs(x - mid0) || x - y);
  order.forEach((i) => {
    const f = faces[i] as Face;
    const a = (i - mid0) * step;
    const lift = f === 'p1' ? 16 : 0; // the focus tile rises out of the fan
    const x = cx - TW / 2;
    const y = cy - R - TH - lift;
    tiles +=
      `<g transform="rotate(${n(a)} ${cx} ${cy})">` +
      // soft shadow, lacquer back (the tile's thickness), ivory face
      `<rect x="${x + 5}" y="${y + 9}" width="${TW}" height="${TH}" rx="9" fill="${shadowInk(P)}" fill-opacity="${P.dark ? 0.35 : 0.12}"/>` +
      `<rect x="${x}" y="${y + 6}" width="${TW}" height="${TH}" rx="9" fill="url(#${p}bk)" stroke="${edge(lacquer, P)}" stroke-width="1.5"/>` +
      `<rect x="${x}" y="${y}" width="${TW}" height="${TH}" rx="9" fill="url(#${p}iv)" stroke="${ivoryEdge}" stroke-width="1.5"/>` +
      `<g transform="translate(${x} ${y})">${face(f)}</g>` +
      gloss(x + 14, y + 12, 9, 4, -35, 0.5) +
      `</g>`;
  });

  const fan = `<g transform="translate(${n(cx * (1 - S))} ${n(fy * (1 - S))}) scale(${S})">${tiles}</g>`;

  // warm glow behind the fan, a soft floor shadow and gold sparkles above it
  const glow = `<ellipse cx="${cx}" cy="${fy}" rx="170" ry="120" fill="url(#${p}gw)"/>`;
  const floor = `<ellipse cx="${cx + 6}" cy="${fy + 92}" rx="190" ry="20" fill="url(#${p}sh)"/>`;
  const r = rng(88);
  let sparkles = '';
  for (let i = 0; i < 9; i++) {
    const a = -Math.PI + ((i + 0.5) / 9) * Math.PI + (r() - 0.5) * 0.25;
    const d = 118 + r() * 34;
    const sx = cx + Math.cos(a) * d * 1.35;
    const sy = fy - 6 + Math.sin(a) * d;
    sparkles += `<polygon points="${star(sx, sy, 3.5 + r() * 4, 1.3 + r() * 0.8, 4)}" fill="${i % 2 ? gold : tint(gold, 0.35)}"/>`;
  }

  const defs =
    `<defs>${bg.defs}` +
    body(`${p}iv`, ivory, 0.5, 0.12, 0.6, 1) +
    body(`${p}bk`, lacquer, 0.3, 0.35, 0.6, 1) +
    body(`${p}md`, P.gold, 0.45, 0.3, 0.5, 1) +
    fade(`${p}gw`, gold, P.dark ? 0.35 : 0.45) +
    fade(`${p}sh`, shadowInk(P), P.dark ? 0.5 : 0.16) +
    `</defs>`;

  return defs + bg.body + glow + floor + fan + sparkles;
}
